import { AxiosInstance, CancelToken } from 'axios';
import { anonymousPortalInstance, createContextAwareHandlers } from './instance';

export const getInfo = (instance: AxiosInstance) => async (): Promise<User> => {
  const { data } = await instance.get<User>('/user');
  return data;
};

export const createMetadata = (instance: AxiosInstance) => async (
  internalName: string,
  displayName: string,
  type: string,
  customerId: string,
  listValues?: string[],
  isMandatory?: boolean,
): Promise<Customer> => {
  const { data } = await instance.post<Customer>(`/customers/${customerId}/custommetadata`, {
    internalName,
    type,
    displayName,
    listValues,
    isMandatory,
  });
  return data;
};

export const modifyMetadata = (instance: AxiosInstance) => async (
  name: string,
  displayName: string,
  customerId: string,
  type?: string,
  listValues?: string[],
  isMandatory?: boolean,
): Promise<Customer> => {
  const { data } = await instance.put<Customer>(`/customers/${customerId}/custommetadata/${name}`, {
    displayName,
    type,
    listValues,
    isMandatory,
  });
  return data;
};

export const deleteMetadata = (instance: AxiosInstance) => async (name: string, customerId: string): Promise<void> => {
  await instance.delete<Customer>(`/customers/${customerId}/custommetadata/${name}`);
};

type UserInformation = 'name' | 'preferredName' | 'gender' | 'otherGender' | 'dateOfBirth' | 'language';

export const modifyUserInformation = (instance: AxiosInstance) => async (name: UserInformation, value: string, customerId: string): Promise<User> => {
  const { data } = await instance.put<User>(`/user/accounts/${customerId}/profile/${name}`, `"${value}"`, {
    headers: {
      'Content-Type': 'plain/text',
    },
  });
  return data;
};

export const modifyAvatarCustomer = (instance: AxiosInstance) => async (avatar: File, customerId: string): Promise<Account> => {
  const response = await instance.put<User>(`/user/accounts/${customerId}/profile/picture`, avatar, {
    headers: {
      'Content-Type': avatar?.type,
    },
  });

  const account = response.data.accounts.find(a => a.customerId === customerId) as Account;
  return account;
};

export const modifyAvatarPartner = (instance: AxiosInstance) => async (avatar: File, partnershipId: string): Promise<Account> => {
  const response = await instance.put<User>(`/user/accounts/${partnershipId}/profile/picture`, avatar, {
    headers: {
      'Content-Type': avatar?.type,
    },
  });

  const account = response.data.accounts.find(a => a.partnershipId === partnershipId) as Account;
  return account;
};

export const deleteAvatar = (instance: AxiosInstance) => async (id: string): Promise<void> => {
  await instance.delete<User>(`/user/accounts/${id}/profile/picture`);
};

export const modifyBillingInformation = (instance: AxiosInstance) => async (
  billingInfromation: BillingInformation,
  weight: number,
  customerId: string,
  paymentMethodId: string,
): Promise<Customer> => {
  const { data } = await instance.put<Customer>(`customers/${customerId}/paymentmethods/${paymentMethodId}`, {
    weight: weight > 0 ? weight ?? 1 : 1,
    invoice: billingInfromation,
  });
  return data;
};

export const updateCustomerInformation = (instance: AxiosInstance) => async (
  customerInformation: CustomerInformation,
  customerId: string,
): Promise<CustomerInformation> => {
  const { data } = await instance.put<CustomerInformation>(`customers/${customerId}/info`, customerInformation);
  return data;
};

export const deleteUserAccount = (instance: AxiosInstance) => async (id: string): Promise<void> => {
  await instance.delete<User>(`/user/accounts/${id}`);
};

export const deleteCustomer = (instance: AxiosInstance) => async (customerId: string): Promise<void> => {
  await instance.delete<CustomerUser>(`/customers/${customerId}`);
};

export const transferOwnership = (instance: AxiosInstance) => async (customerId: string, userId: string): Promise<unknown> => {
  const { data } = await instance.put<User>(`/customers/${customerId}/users/${userId}`, {
    customerRoleId: 'owner',
  });

  return data;
};

export const modifyEmailSubscription = (instance: AxiosInstance) => async (
  values: Partial<MailNotifications>,
  customerId: string,
): Promise<MailNotifications> => {
  const { data } = await instance.put<MailNotifications>(`/user/accounts/${customerId}/mailnotifications`, values);

  return data;
};

interface CustomerBatchResponseOk {
  jobId: string;
  partnershipId: string;
}

interface CustomerBatchResponseError {
  message: string;
  data: string[];
}

export interface CustomerData {
  ownerName: string | null;
  email: string;
  name: string;
  currency: string;
  customerReference: string;
  country?: string;
  state?: string;
  language?: string;
}
export type CustomerBatchRequest = CustomerData[];

export const createMultipleCustomers = (instance: AxiosInstance) => async (
  file: File | CustomerBatchRequest,
  sourceToken: CancelToken,
  partnershipId: string,
  isTest: boolean,
): Promise<CustomerBatchResponseOk & CustomerBatchResponseError> => {
  const { data } = await instance.post<CustomerBatchResponseOk & CustomerBatchResponseError>(
    `/partnerships/${partnershipId}/customers/batch?testRun=${isTest ? 'true' : 'false'}`,
    file,
    {
      headers: {
        'Content-Type': file instanceof File ? file.type : 'application/json',
      },
      cancelToken: sourceToken,
    },
  );

  return data;
};

interface CreateCustomer {
  name: string;
  organizationNumber?: string;
  type: string;
  payment: {
    currency: string;
  };
  info: {
    address: Address;
    sizeEmployees?: {
      from: number | null;
      to: number | null;
    };
  };
  settings: {
    termsAndConditions: {
      accepted: boolean;
    };
    language: string;
  };
}

export interface InviteUserRequest {
  partnershipId: string;
  name: string;
  email: string;
  type: string;
  organizationNumber: string;
  payment: {
    currency: string;
  };
  settings: {
    termsAndConditions: {
      accepted: boolean;
    };
  };
}

export const createCustomer = (instance: AxiosInstance) => async (body: CreateCustomer): Promise<Customer> => {
  const { data } = await instance.post<Customer>(`/customers`, body);
  return data;
};

export const inviteUser = (instance: AxiosInstance) => async (body: InviteUserRequest): Promise<Customer> => {
  const { data } = await instance.post<Customer>(`/customers`, body);
  return data;
};

export const getCustomers = (instance: AxiosInstance) => async (): Promise<Customer[]> => {
  const { data } = await instance.get<Customer[]>(`/customers`);
  return data;
};

export const getInvitedUser = async (id: string): Promise<Invitation> => {
  const { data } = await anonymousPortalInstance.get<Invitation>(`/invites/${id}`);
  return data;
};

export const completeInvitation = (instance: AxiosInstance) => async (id: string, code: string, mergeWithCustomerId?: string): Promise<unknown> => {
  const { data } = await instance.post<unknown>(`/invites/${id}/claim`, {
    claimCode: code,
    mergeWithCustomerId,
  });
  return data;
};

export const getAttributionSyncStatus = (instance: AxiosInstance) => async (customerId: string, orderId: string): Promise<unknown> => {
  const { data } = await instance.get<unknown>(`/customers/${customerId}/orders/${orderId}/attribution`);
  return data;
};

export const getAttributionForCustomer = (instance: AxiosInstance) => async (customerId: string): Promise<Attribution> => {
  const { data } = await instance.get<Attribution>(`/customers/${customerId}/attribution`);
  return data;
};
export const getAttributionForPartnership = (instance: AxiosInstance) => async (partnershipId: string): Promise<Attribution> => {
  const { data } = await instance.get<Attribution>(`/partnerships/${partnershipId}/attribution`);
  return data;
};

export const getAttributionForUser = (instance: AxiosInstance) => async (): Promise<Attribution> => {
  const { data } = await instance.get<Attribution>(`/user/attribution`);
  return data;
};

export const getGuestAccessToken = async (inviteId: string, claimCode: string): Promise<string> => {
  const { data } = await anonymousPortalInstance.post<{ accessToken: string }>(`/invites/${inviteId}/token`, { claimCode });
  return data.accessToken;
};

export const getCustomer = (instance: AxiosInstance) => async (customerId: string): Promise<Customer> => {
  const { data } = await instance.get<Customer>(`/customers/${customerId}`);
  return data;
};

export const getPartnership = (instance: AxiosInstance) => async (id: string): Promise<Partnership> => {
  const { data } = await instance.get<Partnership>(`/partnerships/${id}`);
  return data;
};

export const updatePartnershipInvoiceInformation = (instance: AxiosInstance) => async (
  newBillinData: BillingInformation,
  partnershiId: string,
): Promise<BillingInformation> => {
  const { data } = await instance.put<BillingInformation>(`/partnerships/${partnershiId}/paymentmethods`, { invoice: newBillinData });
  return data;
};

export const updateCustomer = (instance: AxiosInstance) => async ({ id, ...body }: UpdateCustomer): Promise<Customer> => {
  const { data } = await instance.put<Customer>(`/customers/${id}`, body);
  return data;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getSettingsForUser = (_: AxiosInstance) => async (): Promise<SettingsResponse> => {
  const { data } = await anonymousPortalInstance.get<SettingsResponse>('/settings', {
    headers: {
      'x-functions-key': process.env.REACT_APP_WECHOOOSE_API_KEY,
    },
  });
  return data;
};

interface UpdatePartnership {
  id: string;
  settings?: {
    public?: boolean;
  };
}

export const updatePartnership = (instance: AxiosInstance) => async ({ id, ...body }: UpdatePartnership): Promise<Partnership> => {
  //TODO: better handling for updating partnership
  const { data } = await instance.put<Partnership>(`/partnerships/${id}/settings.public`, `${body.settings?.public}`);
  return data;
};

export const getInvitedUserByClaimCode = async (claimCode: string): Promise<Invitation> => {
  const { data } = await anonymousPortalInstance.post<Invitation>(`/gift`, { claimCode });
  return data;
};

export const getPartnersCustomers = (instance: AxiosInstance) => async (
  partnershipId: string,
  query: string,
  limit: number,
  offset: number,
): Promise<PartnershipCustomers> => {
  const { data } = await instance.get<PartnershipCustomers>(`/partnerships/${partnershipId}/customers`, {
    params: {
      query,
      limit,
      offset,
    },
  });
  return data;
};

export const getReceivedGiftcards = (instance: AxiosInstance) => async (customerId: string): Promise<GiftCardData[]> => {
  const { data } = await instance.get<GiftCardData[]>(`/customers/${customerId}/gifts`);
  return data;
};

export const updateFacilitatedOrdersAutoApprove = (instance: AxiosInstance) => async (customerId: string, autoApprove: boolean): Promise<unknown> => {
  const { data } = await instance.put<unknown>(`/customers/${customerId}/approvefacilitatedordersfromcurrentpartnership`, `${autoApprove}`);
  return data;
};

export const acceptTermsCustomer = (instance: AxiosInstance) => async (customerId: string): Promise<SettingsResponse> => {
  const { data } = await instance.post<SettingsResponse>(`/customers/${customerId}/terms/approve`, {});

  return data;
};

export const acceptTermsPartnership = (instance: AxiosInstance) => async (partnershipId: string): Promise<SettingsResponse> => {
  const { data } = await instance.post<SettingsResponse>(`/partnerships/${partnershipId}/terms/approve`, {});

  return data;
};

export const acceptPrivacyPolicy = (instance: AxiosInstance) => async (accountId: string): Promise<PrivacyPolicyResponse> => {
  const { data } = await instance.post(`/user/accounts/${accountId}/privacyPolicy/approve`, {});

  return data;
};

export const getJobStatus = (instance: AxiosInstance) => async <T>(
  partnershipId: string,
  jobId: string,
  sourceToken: CancelToken,
): Promise<JobStatusResponse<T>> => {
  const { data } = await instance.get<JobStatusResponse<T>>(`/partnerships/${partnershipId}/jobs/${jobId}`, { cancelToken: sourceToken });
  return data;
};

const handlers = {
  getInfo,
  createMetadata,
  modifyMetadata,
  deleteMetadata,
  modifyUserInformation,
  modifyAvatarCustomer,
  modifyAvatarPartner,
  deleteAvatar,
  modifyBillingInformation,
  deleteUserAccount,
  transferOwnership,
  modifyEmailSubscription,
  createCustomer,
  createMultipleCustomers,
  completeInvitation,
  getAttributionSyncStatus,
  getAttributionForCustomer,
  getAttributionForPartnership,
  getAttributionForUser,
  getCustomer,
  getPartnership,
  updateCustomer,
  updatePartnership,
  getReceivedGiftcards,
  getSettingsForUser,
  deleteCustomer,
  getPartnersCustomers,
  updateFacilitatedOrdersAutoApprove,
  updateCustomerInformation,
  updatePartnershipInvoiceInformation,
  acceptTermsCustomer,
  acceptTermsPartnership,
  acceptPrivacyPolicy,
  inviteUser,
  getCustomers,
  getJobStatus,
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const createUserApi = (context: PortalContext) => {
  return createContextAwareHandlers(context, handlers);
};

export default createUserApi;
